<template>
  <!--
    Comptabilité : ce que l'école attendait sur la période, ce qu'elle a
    encaissé, et le détail de chaque versement.

    La période se choisit d'une seule façon à la fois — un mois, ou une date
    de début et de fin — comme l'API l'attend (voir `reportParams`).
  -->
  <div class="container-fluid py-3 acc-page">
    <!-- ======================== En-tête ======================== -->
    <div class="acc-toolbar">
      <!-- Le titre de la page est déjà dans la barre du haut : ici, la
           période affichée, que l'on vérifie avant de lire les montants. -->
      <div class="acc-heading">
        <span class="acc-sub">{{ $t("accounting.period") }}</span>
        <h5 class="mb-0">
          <i class="bi bi-calendar3 me-2"></i>{{ appliedPeriodLabel }}
        </h5>
      </div>

      <div class="acc-period">
        <select
          v-model="classroomId"
          class="acc-field acc-classroom"
          :aria-label="$t('accounting.classroom')"
          @change="onClassroomChange"
        >
          <option value="">{{ $t("accounting.all_classrooms") }}</option>
          <option v-for="classroom in classrooms" :key="classroom.id" :value="classroom.id">
            {{ classroom.label }}
          </option>
        </select>

        <div class="acc-switch" role="group" :aria-label="$t('accounting.period')">
          <button
            type="button"
            :class="{ 'is-active': mode === PERIOD_MODE.MONTH }"
            :aria-pressed="mode === PERIOD_MODE.MONTH"
            @click="setMode(PERIOD_MODE.MONTH)"
          >
            {{ $t("accounting.by_month") }}
          </button>
          <button
            type="button"
            :class="{ 'is-active': mode === PERIOD_MODE.RANGE }"
            :aria-pressed="mode === PERIOD_MODE.RANGE"
            @click="setMode(PERIOD_MODE.RANGE)"
          >
            {{ $t("accounting.by_range") }}
          </button>
        </div>

        <!-- Un mois de l'année en cours : l'année est sous-entendue, seul le
             nom du mois s'affiche. Le changement recharge aussitôt. -->
        <select
          v-if="mode === PERIOD_MODE.MONTH"
          v-model="month"
          class="acc-field acc-month"
          :aria-label="$t('accounting.month')"
          @change="load"
        >
          <option v-for="item in months" :key="item" :value="item">
            {{ monthLabel(item, false) }}
          </option>
        </select>

        <!-- Une plage : rien n'est envoyé tant que les deux dates ne sont pas
             choisies, d'où le bouton plutôt qu'un rechargement à chaque saisie. -->
        <form v-else class="acc-range" @submit.prevent="applyRange">
          <!-- Le champ natif s'affiche dans le format du poste (mois au milieu
               en anglais US) : son texte est masqué, et la date réécrite
               par-dessus en JJ/MM/AAAA. Le calendrier reste celui du
               navigateur. -->
          <label class="acc-range-field">
            <span>{{ $t("accounting.start_date") }}</span>
            <span class="acc-date">
              <input
                v-model="startDate"
                type="date"
                class="acc-field acc-date-native"
                :min="bounds.min"
                :max="endDate || bounds.max"
                required
                @click="openPicker"
              />
              <span class="acc-date-text" aria-hidden="true">{{ displayDay(startDate) }}</span>
            </span>
          </label>
          <label class="acc-range-field">
            <span>{{ $t("accounting.end_date") }}</span>
            <span class="acc-date">
              <input
                v-model="endDate"
                type="date"
                class="acc-field acc-date-native"
                :min="startDate || bounds.min"
                :max="bounds.max"
                required
                @click="openPicker"
              />
              <span class="acc-date-text" aria-hidden="true">{{ displayDay(endDate) }}</span>
            </span>
          </label>
          <button type="submit" class="acc-apply" :disabled="!rangeIsValid || loading">
            {{ $t("accounting.apply") }}
          </button>
        </form>

        <button
          type="button"
          class="acc-icon-btn"
          :title="$t('accounting.refresh')"
          :aria-label="$t('accounting.refresh')"
          :disabled="loading"
          @click="load"
        >
          <i class="bi bi-arrow-clockwise" :class="{ 'acc-spin': loading }"></i>
        </button>

        <button
          type="button"
          class="acc-apply"
          :title="$t('accounting.download_title')"
          :disabled="loading || failed"
          @click="print"
        >
          <i class="bi bi-download me-1"></i>{{ $t("accounting.download") }}
        </button>
      </div>
    </div>

    <!-- ======================== Erreur ======================== -->
    <div class="card acc-state" v-if="failed">
      <i class="bi bi-exclamation-triangle acc-state-icon is-error"></i>
      <h6 class="mb-1">{{ $t("accounting.error_title") }}</h6>
      <p class="mb-3">{{ $t("accounting.error_text") }}</p>
      <button type="button" class="acc-apply" @click="load">
        {{ $t("accounting.retry") }}
      </button>
    </div>

    <template v-else>
      <!-- ======================== Totaux ======================== -->
      <div class="acc-kpis">
        <div class="card acc-kpi">
          <span class="acc-kpi-label">{{ $t("accounting.total_expected") }}</span>
          <span class="acc-kpi-value" v-if="!loading">{{ money(report.summary.expected) }}</span>
          <span class="acc-skeleton" v-else></span>
          <!-- Nombre de transactions de la période : le chiffre seul, le
               libellé complet dans la bulle (survol ou focus clavier). -->
          <span
            v-if="!loading"
            class="acc-kpi-count"
            tabindex="0"
            :data-tooltip="$t('accounting.transactions_count', { count: report.count })"
            :aria-label="$t('accounting.transactions_count', { count: report.count })"
          >
            {{ report.count }}
          </span>
        </div>

        <div class="card acc-kpi is-collected">
          <span class="acc-kpi-label">{{ $t("accounting.total_collected") }}</span>
          <span class="acc-kpi-value" v-if="!loading">{{ money(report.summary.collected) }}</span>
          <span class="acc-skeleton" v-else></span>
        </div>

        <div class="card acc-kpi is-pending">
          <span class="acc-kpi-label">{{ $t("accounting.total_pending") }}</span>
          <span class="acc-kpi-value" v-if="!loading">{{ money(report.summary.pending) }}</span>
          <span class="acc-skeleton" v-else></span>
        </div>

        <!-- Le taux dit en un chiffre où en est le recouvrement : c'est la
             question que pose un comptable avant de lire le détail. -->
        <div class="card acc-kpi">
          <span class="acc-kpi-label">{{ $t("accounting.collection_rate") }}</span>
          <template v-if="!loading">
            <span class="acc-kpi-value">{{ report.summary.rate }} %</span>
            <div
              class="acc-progress"
              role="progressbar"
              :aria-valuenow="report.summary.rate"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <span :style="{ width: `${report.summary.rate}%` }"></span>
            </div>
          </template>
          <span class="acc-skeleton" v-else></span>
        </div>
      </div>

      <!-- ======================== Historique ======================== -->
      <div class="card acc-history">
        <div class="acc-history-head">
          <h6 class="mb-0">{{ $t("accounting.history") }}</h6>

          <div class="acc-history-tools">
            <label class="acc-search">
              <i class="bi bi-search"></i>
              <input
                v-model="search"
                type="search"
                :placeholder="$t('accounting.search')"
                :aria-label="$t('accounting.search')"
              />
            </label>
            <select v-model="tone" class="acc-field" :aria-label="$t('accounting.status')">
              <option value="">{{ $t("accounting.all_status") }}</option>
              <option :value="STATUS_TONE.SETTLED">{{ $t("accounting.status_settled") }}</option>
              <option :value="STATUS_TONE.PENDING">{{ $t("accounting.status_pending") }}</option>
            </select>
          </div>
        </div>

        <div class="acc-loading" v-if="loading">
          <Spinner />
        </div>

        <div class="acc-empty" v-else-if="!report.transactions.length">
          <i class="bi bi-receipt"></i>
          <p class="mb-0">{{ $t("accounting.empty") }}</p>
        </div>

        <div class="acc-empty" v-else-if="!visibleTransactions.length">
          <i class="bi bi-funnel"></i>
          <p class="mb-0">{{ $t("accounting.no_match") }}</p>
        </div>

        <template v-else>
          <div class="acc-table-wrap">
            <table class="acc-table">
              <thead>
                <tr>
                  <th>{{ $t("accounting.matricule") }}</th>
                  <th>{{ $t("accounting.label") }}</th>
                  <th>{{ $t("accounting.provider") }}</th>
                  <th>{{ $t("accounting.reference") }}</th>
                  <th class="text-end">{{ $t("accounting.amount") }}</th>
                  <th class="text-end">{{ $t("accounting.status") }}</th>
                  <th class="text-end">{{ $t("accounting.date") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(transaction, index) in pagedTransactions"
                  :key="transactionKey(transaction, pageOffset + index)"
                >
                  <td class="acc-mono">{{ transaction.registrationNumber || "—" }}</td>

                  <td>
                    <span class="acc-main">{{ transaction.label || "—" }}</span>
                    <span class="acc-muted">
                      {{
                        transaction.isTuition
                          ? $t("accounting.type_tuition")
                          : $t("accounting.type_other")
                      }}
                    </span>
                  </td>

                  <td>
                    <span class="acc-provider">
                      <img
                        v-if="providerLogo(transaction)"
                        :src="providerLogo(transaction)"
                        :alt="providerName(transaction)"
                      />
                      <i
                        v-else-if="transaction.providerKey === MANUAL_PROVIDER"
                        class="bi bi-cash-coin acc-provider-icon"
                      ></i>
                      <span>{{ providerName(transaction) }}</span>
                    </span>
                  </td>

                  <td class="acc-mono">{{ transaction.reference || "—" }}</td>

                  <td class="text-end acc-mono">
                    <span class="acc-main acc-amount">{{ money(transaction.amount) }}</span>
                    <!-- Un versement partiel n'est qu'une part du paiement :
                         sans la mention, on croirait le paiement entier. -->
                    <span
                      class="acc-split"
                      v-if="transaction.isSplit"
                      :title="$t('accounting.split_title')"
                    >
                      {{ $t("accounting.split") }}
                    </span>
                  </td>

                  <td class="text-end">
                    <span class="acc-badge" :class="`is-${transaction.tone}`">
                      {{ statusLabel(transaction.status) }}
                    </span>
                  </td>

                  <td class="text-end acc-mono">
                    <template v-if="transaction.date">
                      <span class="acc-main">{{ formatDay(transaction.date) }}</span>
                      <span class="acc-muted">{{ formatTime(transaction.date) }}</span>
                    </template>
                    <span class="acc-muted" v-else>—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="acc-pager" v-if="pageCount > 1">
            <span class="acc-sub">
              {{
                $t("accounting.page_of", { page: currentPage, total: pageCount })
              }}
            </span>
            <div class="acc-pager-buttons">
              <button
                type="button"
                class="acc-icon-btn"
                :disabled="currentPage === 1"
                :aria-label="$t('button.previous')"
                @click="currentPage -= 1"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
              <button
                type="button"
                class="acc-icon-btn"
                :disabled="currentPage === pageCount"
                :aria-label="$t('button.next')"
                @click="currentPage += 1"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!--
      Version imprimable : invisible à l'écran, seule visible à l'impression.

      Téléportée dans <body> pour que l'impression puisse masquer tout le
      reste (barre latérale, en-tête) d'un seul sélecteur. Elle reprend la
      liste ENTIÈRE, filtres compris, et non la seule page affichée.
    -->
    <teleport to="body">
      <div class="acc-print">
        <header class="acc-print-head">
          <div class="acc-print-school">
            <img v-if="school.logo" class="acc-print-logo" :src="school.logo" :alt="school.name" />
            <div>
              <p class="acc-print-school-name">{{ school.name || $t("accounting.school_unknown") }}</p>
              <p class="acc-print-line" v-if="schoolAddress">{{ schoolAddress }}</p>
              <p class="acc-print-line" v-if="schoolContacts">{{ schoolContacts }}</p>
              <!-- La période en titre : c'est elle qu'on cherche du regard en
                   classant les rapports. -->
              <p class="acc-print-period">
                <span v-for="part in printPeriodParts" :key="part">{{ part }}</span>
              </p>
            </div>
          </div>
          <div class="acc-print-meta">
            <p class="acc-print-title">{{ $t("accounting.print_title") }}</p>
            <p class="acc-print-line">{{ appliedClassroomLabel }}</p>
            <p class="acc-print-line">{{ $t("accounting.issued_on", { date: today }) }}</p>
          </div>
        </header>

        <div class="acc-print-totals">
          <div class="acc-print-total">
            <span>{{ $t("accounting.total_expected") }}</span>
            <strong>{{ money(report.summary.expected) }}</strong>
          </div>
          <div class="acc-print-total">
            <span>{{ $t("accounting.total_collected") }}</span>
            <strong>{{ money(report.summary.collected) }}</strong>
          </div>
          <div class="acc-print-total">
            <span>{{ $t("accounting.total_pending") }}</span>
            <strong>{{ money(report.summary.pending) }}</strong>
          </div>
          <div class="acc-print-total">
            <span>{{ $t("accounting.collection_rate") }}</span>
            <strong>{{ report.summary.rate }} %</strong>
          </div>
        </div>

        <table class="acc-print-table" v-if="visibleTransactions.length">
          <thead>
            <tr>
              <th>{{ $t("accounting.matricule") }}</th>
              <th>{{ $t("accounting.label") }}</th>
              <th>{{ $t("accounting.provider") }}</th>
              <th>{{ $t("accounting.reference") }}</th>
              <th class="text-end">{{ $t("accounting.amount") }}</th>
              <th class="text-end">{{ $t("accounting.status") }}</th>
              <th class="text-end">{{ $t("accounting.date") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(transaction, index) in visibleTransactions"
              :key="transactionKey(transaction, index)"
            >
              <td>{{ transaction.registrationNumber || "—" }}</td>
              <td>
                {{ transaction.label || "—" }}
                <span class="acc-print-sub">
                  {{ transaction.isTuition ? $t("accounting.type_tuition") : $t("accounting.type_other") }}
                </span>
              </td>
              <td>{{ providerName(transaction) }}</td>
              <td>{{ transaction.reference || "—" }}</td>
              <td class="text-end acc-print-amount">
                {{ money(transaction.amount) }}
                <span class="acc-print-sub" v-if="transaction.isSplit">{{ $t("accounting.split") }}</span>
              </td>
              <td class="text-end acc-print-status" :class="`is-${transaction.tone}`">
                {{ statusLabel(transaction.status) }}
              </td>
              <td class="text-end acc-print-date">
                {{ transaction.date ? `${formatDay(transaction.date)} ${formatTime(transaction.date)}` : "—" }}
              </td>
            </tr>
          </tbody>
        </table>
        <p class="acc-print-empty" v-else>
          {{ report.transactions.length ? $t("accounting.no_match") : $t("accounting.empty") }}
        </p>

        <p class="acc-print-sum" v-if="visibleTransactions.length">
          {{ $t("accounting.print_listed_total", { count: visibleTransactions.length }) }}
          <strong>{{ money(visibleTotal) }}</strong>
        </p>
      </div>
    </teleport>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner.vue";
import api_config from "@/config";
import { getAccountingReport, getGrades } from "@/utils/services";
import { createPrintSession } from "@/utils/printSession";
import { loadSchoolProfile, schoolAddressLine, schoolProfile } from "@/utils/schoolProfile";
import {
  MANUAL_PROVIDER,
  PERIOD_MODE,
  STATUS_TONE,
  classroomOptions,
  filterTransactions,
  dayKeyOf,
  isValidRange,
  monthKeyOf,
  monthsOfYear,
  normalizeAccountingReport,
  providerLabelOf,
  rangeBounds,
  reportParams,
  transactionKey,
} from "@/utils/accountingReport";

const PAGE_SIZE = 25;

/* Opérateurs connus : mêmes logos que l'historique des transactions. */
const PROVIDERS = {
  "mtn-momo": { name: "MTN MoMo", logo: require("@/assets/img/mtn1.jpeg") },
  "orange-money": { name: "Orange Money", logo: require("@/assets/img/orange1.jpeg") },
  bicec: { name: "BICEC", logo: require("@/assets/img/bicec.png") },
  sara: { name: "SARA", logo: require("@/assets/img/afriland.png") },
  "express-union": { name: "Express Union", logo: require("@/assets/img/express.png") },
  me2u: { name: "Me2U", logo: require("@/assets/img/me2u.png") },
};

/* Statuts renvoyés par l'API, et leur libellé. */
const STATUS_LABELS = {
  settled: "accounting.status_settled",
  paid: "accounting.status_paid",
  success: "accounting.status_paid",
  pending: "accounting.status_pending",
  accepted: "accounting.status_accepted",
  overdue: "accounting.status_overdue",
  failed: "accounting.status_failed",
};

export default {
  name: "accounting-page",
  components: { Spinner },
  data() {
    const today = new Date();
    return {
      PERIOD_MODE,
      STATUS_TONE,
      MANUAL_PROVIDER,
      currency: api_config.CURRENCY_CODE || "XAF",
      school: schoolProfile,
      classrooms: [],
      /* Chaîne vide : toutes les classes. */
      classroomId: "",
      mode: PERIOD_MODE.MONTH,
      months: monthsOfYear(today),
      month: monthKeyOf(today),
      bounds: rangeBounds(today),
      // Plage proposée par défaut : du 1er du mois à aujourd'hui.
      startDate: dayKeyOf(new Date(today.getFullYear(), today.getMonth(), 1)),
      endDate: dayKeyOf(today),
      /* Période de la dernière requête : le titre décrit ce qui a été
         demandé, pas ce qui est en cours de saisie. */
      applied: { mode: PERIOD_MODE.MONTH, month: monthKeyOf(today), classroomId: "" },
      report: normalizeAccountingReport(null),
      loading: false,
      failed: false,
      search: "",
      tone: "",
      currentPage: 1,
      /* Numéro de la dernière requête : une réponse plus ancienne arrivée en
         retard ne doit pas écraser la plus récente. */
      requestId: 0,
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale === "fr" ? "fr-FR" : "en-US";
    },
    rangeIsValid() {
      return isValidRange(this.startDate, this.endDate, this.bounds);
    },
    appliedPeriodLabel() {
      if (this.applied.mode === PERIOD_MODE.RANGE) {
        return this.$t("accounting.range_label", {
          start: this.formatIsoDay(this.applied.startDate),
          end: this.formatIsoDay(this.applied.endDate),
        });
      }
      return this.monthLabel(this.applied.month);
    },
    /* Période du PDF, en morceaux insécables : une plage passe à la ligne
       entre ses deux dates, jamais au milieu de l'une d'elles. */
    printPeriodParts() {
      if (this.applied.mode === PERIOD_MODE.RANGE) {
        return [
          this.$t("accounting.range_from", { date: this.formatIsoDay(this.applied.startDate) }),
          this.$t("accounting.range_to", { date: this.formatIsoDay(this.applied.endDate) }),
        ];
      }
      return [this.monthLabel(this.applied.month)];
    },
    appliedClassroomLabel() {
      if (!this.applied.classroomId) return this.$t("accounting.all_classrooms");
      const classroom = this.classrooms.find((item) => item.id === this.applied.classroomId);
      return classroom ? classroom.label : this.$t("accounting.all_classrooms");
    },
    visibleTransactions() {
      return filterTransactions(this.report.transactions, {
        search: this.search,
        tone: this.tone,
      });
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.visibleTransactions.length / PAGE_SIZE));
    },
    pageOffset() {
      return (this.currentPage - 1) * PAGE_SIZE;
    },
    pagedTransactions() {
      return this.visibleTransactions.slice(this.pageOffset, this.pageOffset + PAGE_SIZE);
    },
    visibleTotal() {
      return this.visibleTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
    },
    schoolAddress() {
      return schoolAddressLine();
    },
    schoolContacts() {
      return [this.school.phone, this.school.email].filter(Boolean).join("  ·  ");
    },
    today() {
      return new Date().toLocaleDateString(this.locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
  },
  watch: {
    // Un nouveau filtre peut vider la page courante : on repart de la première.
    search() {
      this.currentPage = 1;
    },
    tone() {
      this.currentPage = 1;
    },
  },
  created() {
    this.printSession = createPrintSession({
      bodyClass: "acc-printing",
      styleId: "acc-print-style",
      pageRule: "@page { size: A4 portrait; margin: 12mm 10mm; }",
    });
  },
  mounted() {
    this.load();
    this.loadClassrooms();
    // L'en-tête imprimé vient de la fiche établissement.
    loadSchoolProfile();
    // Ctrl+P et le menu du navigateur impriment aussi le rapport, pas l'écran.
    window.addEventListener("beforeprint", this.markPrinting);
    window.addEventListener("afterprint", this.unmarkPrinting);
  },
  beforeUnmount() {
    window.removeEventListener("beforeprint", this.markPrinting);
    window.removeEventListener("afterprint", this.unmarkPrinting);
    this.unmarkPrinting();
  },
  methods: {
    transactionKey,
    /**
     * Ouvre la boîte d'impression, d'où l'on enregistre en PDF.
     *
     * Le format est posé avant d'ouvrir la boîte : le navigateur peut arrêter
     * la taille du papier avant d'émettre `beforeprint`.
     */
    print() {
      this.markPrinting();
      window.print();
    },
    markPrinting() {
      // Nom proposé pour le fichier PDF.
      const title = [
        this.$t("accounting.page"),
        this.appliedPeriodLabel,
        this.applied.classroomId ? this.appliedClassroomLabel : "",
        this.school.name,
      ]
        .filter(Boolean)
        .join(" - ");
      this.printSession.start({ title });
    },
    unmarkPrinting() {
      this.printSession.stop();
    },
    setMode(mode) {
      if (this.mode === mode) return;
      this.mode = mode;
      // Le mois recharge immédiatement ; la plage attend « Appliquer ».
      if (mode === PERIOD_MODE.MONTH) this.load();
    },
    applyRange() {
      if (this.rangeIsValid) this.load();
    },
    // Recharge aussitôt ; en mode période, `load` attend des dates valides.
    onClassroomChange() {
      this.load();
    },
    /* Sans la liste, le filtre propose seulement « Toutes les classes ». */
    async loadClassrooms() {
      try {
        const response = await getGrades();
        this.classrooms = classroomOptions(response && response.data);
      } catch (error) {
        console.error("Erreur lors du chargement des classes :", error);
      }
    },
    async load() {
      // Une plage incomplète ou hors bornes n'est jamais envoyée, quel que
      // soit le bouton qui déclenche le chargement (actualiser, classe…).
      if (this.mode === PERIOD_MODE.RANGE && !this.rangeIsValid) return;
      const period =
        this.mode === PERIOD_MODE.RANGE
          ? { mode: PERIOD_MODE.RANGE, startDate: this.startDate, endDate: this.endDate }
          : { mode: PERIOD_MODE.MONTH, month: this.month };
      period.classroomId = this.classroomId;
      const requestId = ++this.requestId;

      this.loading = true;
      this.failed = false;
      this.applied = period;
      try {
        const payload = await getAccountingReport(reportParams(period));
        if (requestId !== this.requestId) return;
        this.report = normalizeAccountingReport(payload);
        this.currentPage = 1;
      } catch (error) {
        if (requestId !== this.requestId) return;
        console.error("Erreur lors du chargement de la comptabilité :", error);
        this.failed = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    money(value) {
      const amount = Number(value || 0).toLocaleString(this.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return `${this.currency} ${amount}`;
    },
    /* « Octobre 2026 », ou « Octobre » seul dans la liste des mois. */
    monthLabel(key, withYear = true) {
      if (!key) return "";
      const [year, month] = key.split("-").map(Number);
      const options = withYear ? { month: "long", year: "numeric" } : { month: "long" };
      const label = new Date(year, month - 1, 1).toLocaleDateString(this.locale, options);
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    /* Borne d'une période, mois en toutes lettres : « 01 octobre 2026 ». */
    formatIsoDay(value) {
      if (!value) return "—";
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day).toLocaleDateString(this.locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    /* Valeur d'un champ de date, « 2026-10-01 » affiché « 01/10/2026 ». */
    displayDay(value) {
      if (!value) return "";
      const [year, month, day] = value.split("-");
      return `${day}/${month}/${year}`;
    },
    /* Un clic n'importe où dans le champ ouvre le calendrier natif : sinon
       il éditerait des segments que l'on ne voit pas. */
    openPicker(event) {
      try {
        event.target.showPicker();
      } catch (error) {
        // Navigateur trop ancien : l'icône du champ ouvre toujours le calendrier.
      }
    },
    /* Date d'une transaction, au format court « 12/10/26 » (jour/mois/année)
       quelle que soit la langue : en anglais américain, « 10/12/26 » se
       lirait comme le 10 décembre. */
    formatDay(date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    },
    formatTime(date) {
      return date.toLocaleTimeString(this.locale, { hour: "2-digit", minute: "2-digit" });
    },
    statusLabel(status) {
      const key = STATUS_LABELS[String(status || "").toLowerCase()];
      return key ? this.$t(key) : status || "—";
    },
    providerLogo(transaction) {
      const provider = PROVIDERS[transaction.providerKey];
      return provider ? provider.logo : null;
    },
    providerName(transaction) {
      if (transaction.providerKey === MANUAL_PROVIDER) {
        return this.$t("accounting.provider_manual");
      }
      const provider = PROVIDERS[transaction.providerKey];
      if (provider) return provider.name;
      return providerLabelOf(transaction.provider) || "—";
    },
  },
};
</script>

<style scoped>
.acc-page {
  color: #e6ebf5;
}

.acc-sub {
  font-size: 12.5px;
  opacity: 0.65;
}

/* --- En-tête et période ------------------------------------------------ */

.acc-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 18px;
}

.acc-period {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.acc-switch {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.acc-switch button {
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  cursor: pointer;
}

.acc-switch button.is-active {
  background: rgba(46, 206, 191, 0.16);
  color: #2ecebf;
  opacity: 1;
}

.acc-field {
  height: 40px;
  padding: 0 12px;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: transparent;
  color: #e6ebf5;
  font-size: 13px;
  color-scheme: dark;
}

.acc-field:focus {
  outline: none;
  border-color: #2ecebf;
}

/* Les options s'affichent dans un menu natif : sans fond explicite, le
   texte clair y serait illisible sur fond blanc. */
.acc-field option {
  background: #141a2f;
  color: #e6ebf5;
}

.acc-month {
  min-width: 190px;
}

.acc-classroom {
  min-width: 190px;
  max-width: 260px;
}

.acc-range {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0;
}

.acc-range-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-size: 11.5px;
  opacity: 0.9;
}

/* Champ de date : texte natif masqué, date réécrite en JJ/MM/AAAA. */
.acc-date {
  position: relative;
  display: inline-block;
}

.acc-date-native {
  width: 150px;
  color: transparent;
  caret-color: transparent;
  cursor: pointer;
}

/* Chrome et Edge : les segments masqués ne doivent pas non plus laisser
   voir leur surlignage au focus. */
.acc-date-native::-webkit-datetime-edit {
  opacity: 0;
}

.acc-date-native::-webkit-calendar-picker-indicator {
  cursor: pointer;
}

.acc-date-text {
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translateY(-50%);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: #e6ebf5;
  pointer-events: none;
}

.acc-apply {
  height: 40px;
  border: none;
  border-radius: 9px;
  background: #2ecebf;
  color: #06231f;
  font-size: 13px;
  font-weight: 600;
  padding: 0 16px;
  cursor: pointer;
}

.acc-apply:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.acc-icon-btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.acc-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.acc-spin {
  display: inline-block;
  animation: acc-spin 0.9s linear infinite;
}

@keyframes acc-spin {
  to {
    transform: rotate(360deg);
  }
}

/* --- Erreur ------------------------------------------------------------ */

.acc-state {
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.acc-state p {
  font-size: 13px;
  opacity: 0.7;
  max-width: 460px;
}

.acc-state-icon {
  font-size: 34px;
  color: #2ecebf;
  margin-bottom: 12px;
}

.acc-state-icon.is-error {
  color: #ff5f7a;
}

/* --- Totaux ------------------------------------------------------------ */

.acc-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.acc-kpi {
  position: relative;
  padding: 16px 18px;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.acc-kpi-count {
  position: absolute;
  top: 14px;
  right: 14px;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  cursor: default;
  outline: none;
}

.acc-kpi-count:focus-visible {
  box-shadow: 0 0 0 2px #2ecebf;
}

/* Bulle : au-dessus de la pastille, pour ne pas masquer le montant, et
   alignée à droite pour ne pas sortir de la carte. */
.acc-kpi-count::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 5;
  padding: 6px 11px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: #0b1230;
  color: #e6ebf5;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.acc-kpi-count:hover::after,
.acc-kpi-count:focus-visible::after {
  opacity: 1;
}

.acc-kpi.is-collected {
  border-color: rgba(46, 206, 191, 0.4);
}

.acc-kpi.is-pending {
  border-color: rgba(251, 191, 36, 0.4);
}

.acc-kpi-label {
  font-size: 12.5px;
  opacity: 0.65;
}

.acc-kpi-value {
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.acc-kpi.is-collected .acc-kpi-value {
  color: #2ecebf;
}

.acc-kpi.is-pending .acc-kpi-value {
  color: #fbbf24;
}

.acc-progress {
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin-top: 4px;
}

.acc-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #2ecebf;
  transition: width 0.4s ease;
}

.acc-skeleton {
  display: block;
  width: 60%;
  height: 26px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  animation: acc-pulse 1.2s ease-in-out infinite;
}

@keyframes acc-pulse {
  50% {
    opacity: 0.4;
  }
}

/* --- Historique -------------------------------------------------------- */

.acc-history {
  padding: 18px 0 8px;
}

.acc-history-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 20px 14px;
}

.acc-history-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.acc-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  margin: 0;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  font-size: 13px;
}

.acc-search:focus-within {
  border-color: #2ecebf;
}

.acc-search i {
  opacity: 0.55;
}

.acc-search input {
  width: 220px;
  border: none;
  outline: none;
  background: transparent;
  color: #e6ebf5;
}

.acc-loading,
.acc-empty {
  padding: 56px 20px;
  text-align: center;
  font-size: 13px;
}

.acc-empty i {
  display: block;
  font-size: 30px;
  opacity: 0.4;
  margin-bottom: 10px;
}

.acc-empty p {
  opacity: 0.65;
}

.acc-table-wrap {
  overflow-x: auto;
}

.acc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.acc-table th,
.acc-table td {
  padding: 12px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  white-space: nowrap;
  vertical-align: middle;
}

.acc-table th {
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.7;
}

.acc-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.acc-main {
  display: block;
}

.acc-muted {
  display: block;
  font-size: 11.5px;
  opacity: 0.55;
}

.acc-mono {
  font-variant-numeric: tabular-nums;
}

.acc-amount {
  font-weight: 700;
}

.acc-provider {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.acc-provider img {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
}

.acc-provider-icon {
  font-size: 18px;
  color: #2ecebf;
}

.acc-split {
  display: inline-block;
  margin-top: 3px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(94, 152, 228, 0.18);
  color: #8ab6ee;
}

.acc-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
}

.acc-badge.is-settled {
  background: rgba(46, 206, 191, 0.16);
  color: #2ecebf;
}

.acc-badge.is-pending {
  background: rgba(251, 191, 36, 0.16);
  color: #fbbf24;
}

.acc-badge.is-overdue {
  background: rgba(255, 95, 122, 0.16);
  color: #ff5f7a;
}

.acc-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.acc-pager-buttons {
  display: flex;
  gap: 8px;
}

@media (max-width: 1199px) {
  .acc-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .acc-kpis {
    grid-template-columns: minmax(0, 1fr);
  }

  .acc-search input {
    width: 100%;
  }

  .acc-search {
    flex: 1 1 100%;
  }
}
</style>

<style>
/* ------------------------------------------------------------------
   Version imprimable (et donc PDF)
   ------------------------------------------------------------------
   Non scopé : les règles portent sur `body`, hors de ce composant. Tout est
   conditionné à `body.acc-printing`, posé par la séance d'impression : les
   reçus, les certificats et la situation de classe impriment aussi depuis
   l'application, et aucun ne doit casser l'autre.
*/
.acc-print {
  display: none;
}

@media print {
  body.acc-printing {
    height: auto !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  /* `display: none` et non `visibility: hidden` : un élément invisible
     garderait sa place et produirait des pages blanches avant le rapport. */
  body.acc-printing > *:not(.acc-print) {
    display: none !important;
  }

  body.acc-printing .acc-print {
    display: block;
    color: #111111;
    font-size: 10px;
    line-height: 1.4;
  }

  .acc-print p {
    margin: 0;
  }

  .acc-print-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #d0d4dc;
  }

  .acc-print-period {
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.3em;
    margin-top: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.25;
  }

  .acc-print-period span {
    white-space: nowrap;
  }

  .acc-print-school {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .acc-print-logo {
    width: 52px;
    height: 52px;
    object-fit: contain;
  }

  .acc-print-school-name {
    font-size: 14px;
    font-weight: 700;
  }

  .acc-print-line {
    color: #555555;
    font-size: 10.5px;
    margin-top: 2px !important;
  }

  .acc-print-meta {
    text-align: right;
  }

  .acc-print-title {
    font-size: 12.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .acc-print-totals {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    padding: 12px 0;
  }

  .acc-print-total {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px 10px;
    border: 1px solid #d0d4dc;
    border-radius: 8px;
  }

  .acc-print-total span {
    color: #555555;
    font-size: 9.5px;
  }

  .acc-print-total strong {
    font-size: 13px;
  }

  .acc-print-table {
    width: 100%;
    border-collapse: collapse;
  }

  /* Les en-têtes se répètent en haut de chaque page. */
  .acc-print-table thead {
    display: table-header-group;
  }

  .acc-print-table th,
  .acc-print-table td {
    padding: 5px 4px;
    border-bottom: 1px solid #e1e4ea;
    text-align: left;
    vertical-align: top;
  }

  .acc-print-table th {
    font-size: 8.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    color: #444444;
    border-bottom-color: #b9bec8;
  }

  .acc-print-table .text-end {
    text-align: right;
  }

  .acc-print-table tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .acc-print-sub {
    display: block;
    color: #666666;
    font-size: 8.5px;
  }

  .acc-print-amount,
  .acc-print-date {
    white-space: nowrap;
  }
  .acc-print-amount {
    font-weight: 700;
  }

  /* Teintes soutenues : les couleurs de l'écran sont trop claires sur du
     papier blanc. */
  .acc-print-status {
    font-weight: 700;
  }

  .acc-print-status.is-settled {
    color: #0f766e;
  }

  .acc-print-status.is-pending {
    color: #92400e;
  }

  .acc-print-status.is-overdue {
    color: #b91c1c;
  }

  .acc-print-empty {
    padding: 24px 0;
    text-align: center;
    color: #555555;
  }

  .acc-print-sum {
    margin-top: 10px !important;
    text-align: right;
    font-size: 11px;
  }

  .acc-print-sum strong {
    margin-left: 6px;
  }
}
</style>
